// "Send to": who a banner or task goes to. Shared by the Personalized and Task activity
// forms, so what is picked on one is still picked on the other.

import { useEffect, useMemo, useRef, useState } from 'react';
import api, { employeesAPI, managerFocusAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { downloadXlsx } from '../../utils/download';
import { fileToBase64 } from '../../utils/fileToBase64';
import { bannerAPI, type BannerAudienceBody, type DetailRule } from './bannerApi';
import { COUNT_DEBOUNCE_MS, type PickOption, type RecipientMode, type RecipientParseResult, type TeamScope } from './bannerOptions';
import { useBannerDetails } from './useBannerDetails';

/** One row of By details, as far as it has been picked. */
export interface DetailRuleDraft {
  id: number;
  /** detailKey() of the chosen detail; '' until one is picked. */
  fieldKey: string;
  values: string[];
}

let nextRuleId = 1;
const blankRule = (): DetailRuleDraft => ({ id: nextRuleId++, fieldKey: '', values: [] });

export function useBannerAudience(composerOpen: boolean, detailsOpen: boolean = composerOpen) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('employees');
  const [selectedEmpCodes, setSelectedEmpCodes] = useState<string[]>([]);
  const [selectedManagerCodes, setSelectedManagerCodes] = useState<string[]>([]);
  const [teamScope, setTeamScope] = useState<TeamScope>('all');
  const [employeeOptions, setEmployeeOptions] = useState<PickOption[]>([]);
  const [managerOptions, setManagerOptions] = useState<PickOption[]>([]);
  const [pickersLoading, setPickersLoading] = useState(false);
  // A list that could not be read must not read as a list with nobody on it.
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [managersError, setManagersError] = useState<string | null>(null);
  const [pickersAttempt, setPickersAttempt] = useState(0);
  // Which project and attempt the lists were last asked for, so a tab change does not ask again.
  const pickersAsked = useRef<string | null>(null);
  // ── Bulk recipient upload ──
  // Behind a link: the picker stays the primary way in, and this is the escape hatch
  // for "a few people from each of a dozen teams".
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [recipientFile, setRecipientFile] = useState<File | null>(null);
  const [parsingRecipients, setParsingRecipients] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [recipientResult, setRecipientResult] = useState<RecipientParseResult | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  // ── By details ──
  const details = useBannerDetails(detailsOpen, selectedProjectId);
  const [ruleDrafts, setRuleDrafts] = useState<DetailRuleDraft[]>([]);
  // The count, with the question it answers: a change of rules can never lean on an old one.
  const [match, setMatch] = useState<{ body: string; count: number } | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  // Bumped by Try again, to count again after a failure.
  const [matchAttempt, setMatchAttempt] = useState(0);

  // A rule names this project's fields and a pick its people, so another project starts with
  // none, and with its own lists.
  const projectOfPicks = useRef(selectedProjectId);
  useEffect(() => {
    if (projectOfPicks.current === selectedProjectId) return;
    projectOfPicks.current = selectedProjectId;
    setRuleDrafts([]);
    setEmployeeOptions([]);
    setManagerOptions([]);
    setSelectedEmpCodes([]);
    setSelectedManagerCodes([]);
    setEmployeesError(null);
    setManagersError(null);
    setRecipientFile(null);
    setRecipientError(null);
    setRecipientResult(null);
  }, [selectedProjectId]);

  const chooseMode = (next: RecipientMode) => {
    setRecipientMode(next);
    // A first row ready to fill: By details with no rule has nothing to say yet.
    if (next === 'details') setRuleDrafts((prev) => (prev.length ? prev : [blankRule()]));
  };
  const addRule = () => setRuleDrafts((prev) => [...prev, blankRule()]);
  const removeRule = (id: number) => setRuleDrafts((prev) => prev.filter((r) => r.id !== id));
  const updateRule = (id: number, change: Partial<DetailRuleDraft>) =>
    setRuleDrafts((prev) => prev.map((r) => (r.id === id ? { ...r, ...change } : r)));

  const isDetails = recipientMode === 'details';
  const rulesReady = ruleDrafts.length > 0 && ruleDrafts.every((r) => r.fieldKey && r.values.length > 0 && details.fieldOf(r.fieldKey));
  const detailRules: DetailRule[] = rulesReady
    ? ruleDrafts.map((r) => {
        const { scope, source, key } = details.fieldOf(r.fieldKey)!;
        return { scope, source, key, op: 'in', values: r.values };
      })
    : [];

  // How many fit, a moment after the last change. A failed count is never a 0.
  const matchBody =
    isDetails && rulesReady && selectedProjectId != null
      ? JSON.stringify({ projectId: selectedProjectId, rules: detailRules })
      : null;
  const matchCount = match && match.body === matchBody ? match.count : null;
  useEffect(() => {
    setMatchError(null);
    if (!matchBody) {
      setMatchLoading(false);
      return;
    }
    let cancelled = false;
    setMatchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await bannerAPI.matchDetails(JSON.parse(matchBody));
        const count = res.data?.data?.count;
        if (cancelled) return;
        if (typeof count === 'number') setMatch({ body: matchBody, count });
        else setMatchError('Could not count who matches.');
      } catch (err: any) {
        if (!cancelled) setMatchError(err?.response?.data?.message || 'Could not count who matches.');
      } finally {
        if (!cancelled) setMatchLoading(false);
      }
    }, COUNT_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [matchBody, matchAttempt]);

  // What still stops a By details send, in the order the block asks for it.
  const detailsBlocked = !isDetails
    ? null
    : selectedProjectId == null
    ? null
    : details.error
    ? 'The details could not be loaded. Try again below.'
    : details.loaded && details.fieldOptions.length === 0
    ? 'This project has no details to send by.'
    : ruleDrafts.length === 0
    ? 'Add a detail.'
    : !details.loaded
    ? null
    : !rulesReady
    ? 'Pick a value for each detail.'
    : matchError
    ? 'The count failed. Try again below.'
    : matchCount === null
    ? 'Counting…'
    : matchCount === 0
    ? 'Nobody matches these details.'
    : null;

  // Load both picker sources once a banner form is first opened.
  // Employees come from the project roster; managers from the scorecard roster's
  // distinct manager codes (the same source the Red Flags cascade uses).
  useEffect(() => {
    if (!composerOpen || selectedProjectId == null) return;
    const asked = `${selectedProjectId}:${pickersAttempt}`;
    if (pickersAsked.current === asked) return;
    pickersAsked.current = asked;

    let cancelled = false;
    let finished = false;
    setPickersLoading(true);
    setEmployeesError(null);
    setManagersError(null);
    (async () => {
      try {
        const [empRes, mgrRes] = await Promise.allSettled([
          employeesAPI.getProjectEmployees(true, selectedProjectId, false),
          managerFocusAPI.getManagers(selectedProjectId),
        ]);
        if (cancelled) return;

        if (empRes.status === 'fulfilled') {
          const rows: any[] = empRes.value.data?.data?.all ?? empRes.value.data?.data ?? [];
          const seen = new Set<string>();
          const opts: PickOption[] = [];
          for (const r of rows) {
            // The roster endpoint multi-aliases every field for its various
            // consumers — take whichever alias this row happens to carry.
            const code = String(r.emp_code ?? r.empCode ?? r.employeeCode ?? '').trim();
            if (!code || seen.has(code)) continue;
            seen.add(code);
            opts.push({
              code,
              name:
                String(
                  r.emp_name ?? r.empName ?? r.employeeName ?? r.name ?? ''
                ).trim() || code,
            });
          }
          opts.sort((a, b) => a.name.localeCompare(b.name));
          setEmployeeOptions(opts);
        } else {
          setEmployeesError('Could not load the employees.');
        }

        if (mgrRes.status === 'fulfilled') {
          const rows: any[] = mgrRes.value.data?.data ?? [];
          setManagerOptions(
            rows
              .map((m) => ({
                code: String(m.code ?? '').trim(),
                name: String(m.name ?? m.code ?? '').trim(),
                teamSize: Number(m.teamSize ?? 0),
              }))
              .filter((m) => m.code)
              .sort((a, b) => a.name.localeCompare(b.name))
          );
        } else {
          setManagersError('Could not load the managers.');
        }
      } finally {
        finished = true;
        if (!cancelled) setPickersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Left before they arrived (another tab, another project): ask again next time.
      if (!finished) pickersAsked.current = null;
    };
  }, [composerOpen, selectedProjectId, pickersAttempt]);

  const downloadRecipientTemplate = async () => {
    setTemplateLoading(true);
    try {
      const res = await api.get('/announcements/recipients/template', { responseType: 'blob' });
      downloadXlsx('announcement_recipients_template.xlsx', res.data);
    } catch {
      setRecipientError('Could not download the template. Try again.');
    } finally {
      setTemplateLoading(false);
    }
  };

  const clearRecipientFile = () => {
    setRecipientFile(null);
    setRecipientError(null);
    setRecipientResult(null);
  };

  /**
   * Resolve an uploaded sheet into ticked employees.
   *
   * The codes are ADDED to whatever is already selected rather than replacing
   * it — the point of the feature is assembling a list that no single picker
   * query can express, so two uploads (or an upload plus a few manual ticks)
   * have to compose. Clearing is the picker's existing "Clear (n)" button.
   */
  const handleRecipientFile = async (file: File) => {
    setRecipientError(null);
    setRecipientResult(null);

    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setRecipientError('Upload an .xlsx, .xls or .csv file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setRecipientError('That file is over 5 MB — it is a list of codes, not a report.');
      return;
    }
    if (selectedProjectId == null) {
      setRecipientError('Pick a project first — codes are checked against its roster.');
      return;
    }

    setRecipientFile(file);
    setParsingRecipients(true);
    try {
      const b64 = await fileToBase64(file);
      const res = await api.post('/announcements/recipients/parse', {
        data: b64,
        projectId: selectedProjectId,
      });
      const parsed = res.data?.data as RecipientParseResult;

      // The roster endpoint behind the picker and the roster the send checks
      // against are different queries, so they can disagree on both membership
      // and casing. Fold to compare, and keep the picker's casing where it has
      // one — otherwise the count climbs while nothing visibly ticks.
      const byFold = new Map(employeeOptions.map((o) => [o.code.toLowerCase(), o.code]));
      const forUi = parsed.codes.map((c) => byFold.get(c.toLowerCase()) ?? c);

      const missing = parsed.codes
        .filter((c) => !byFold.has(c.toLowerCase()))
        .map((c) => ({ code: c, name: parsed.names?.[c] || c }));
      if (missing.length > 0) {
        setEmployeeOptions((prev) => {
          const known = new Set(prev.map((o) => o.code.toLowerCase()));
          const fresh = missing.filter((o) => !known.has(o.code.toLowerCase()));
          if (fresh.length === 0) return prev;
          return [...prev, ...fresh].sort((a, b) => a.name.localeCompare(b.name));
        });
      }

      // Counted off this render's snapshot for the summary line; the actual
      // merge de-dupes against `prev` so a concurrent tick can't be clobbered.
      const alreadyTicked = new Set(selectedEmpCodes.map((c) => c.toLowerCase()));
      const added = forUi.filter((c) => !alreadyTicked.has(c.toLowerCase())).length;
      setSelectedEmpCodes((prev) => {
        const seen = new Set(prev.map((c) => c.toLowerCase()));
        return [...prev, ...forUi.filter((c) => !seen.has(c.toLowerCase()))];
      });

      if (parsed.codes.length > 0) setRecipientMode('employees');
      setRecipientResult({ ...parsed, added });
    } catch (err: any) {
      setRecipientError(
        err?.response?.data?.message || err?.message || 'Could not read that file.'
      );
    } finally {
      setParsingRecipients(false);
    }
  };

  // For 'project' this is the picker's roster size — a guide for the button
  // label only. The send resolves the project's roster itself, at send time, and so
  // it does for By details.
  const recipientCount =
    recipientMode === 'employees'
      ? selectedEmpCodes.length
      : recipientMode === 'managers'
      ? selectedManagerCodes.length
      : recipientMode === 'details'
      ? matchCount ?? 0
      : employeeOptions.length;

  /**
   * Sum of the selected managers' team sizes. `teamSize` counts DIRECT reports,
   * so this is exact for the 'direct' scope and a floor for 'all' — labelled
   * accordingly rather than presented as a precise figure.
   */
  const estimatedDirectReach = useMemo(() => {
    if (selectedManagerCodes.length === 0) return 0;
    const picked = new Set(selectedManagerCodes);
    return managerOptions
      .filter((m) => picked.has(m.code))
      .reduce((sum, m) => sum + (m.teamSize ?? 0), 0);
  }, [selectedManagerCodes, managerOptions]);

  // "Everyone on this project" needs only the project itself; the roster is resolved at send.
  const hasAudience =
    recipientMode === 'project' ? selectedProjectId != null : recipientCount > 0;
  const audience: BannerAudienceBody =
    recipientMode === 'project'
      ? { allProject: true }
      : recipientMode === 'employees'
      ? { empCodes: selectedEmpCodes }
      : recipientMode === 'details'
      ? { detailRules }
      : { managerCodes: selectedManagerCodes, teamScope };

  return {
    selectedProjectId,
    recipientMode,
    setRecipientMode: chooseMode,
    selectedEmpCodes,
    setSelectedEmpCodes,
    selectedManagerCodes,
    setSelectedManagerCodes,
    teamScope,
    setTeamScope,
    employeeOptions,
    managerOptions,
    pickersLoading,
    employeesError,
    managersError,
    retryPickers: () => setPickersAttempt((n) => n + 1),
    showBulkUpload,
    setShowBulkUpload,
    recipientFile,
    parsingRecipients,
    recipientError,
    recipientResult,
    templateLoading,
    downloadRecipientTemplate,
    clearRecipientFile,
    handleRecipientFile,
    recipientCount,
    estimatedDirectReach,
    hasAudience,
    audience,
    details,
    ruleDrafts,
    addRule,
    removeRule,
    updateRule,
    matchCount,
    matchLoading,
    matchError,
    retryMatch: () => setMatchAttempt((n) => n + 1),
    detailsBlocked,
  };
}

export type BannerAudience = ReturnType<typeof useBannerAudience>;
